<template>
  <div class="credit-card-payments tw-relative tw-flex tw-flex-1">
    <div class="tw-flex tw-flex-1">
      <div class="content tw-flex tw-flex-1 tw-flex-col">
        <!-- Header -->
        <invoices-header
          :title="$t('billing.title.cc_payments')"
          :subtitle="$tc('billing.title.cc_payments.subtitle', getPaymentsMeta.item_count, {
            count: $n(getPaymentsMeta.item_count)
          })"
          class="flex-fixed credit-card-payments__header mb-2"
        />

        <!-- Content -->
        <div
          v-infinite-scroll="retrieveMoreMobile"
          class="tw-w-screen 2sm:tw-w-auto content tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto"
        >
          <transition
            v-if="!$wait.is('loading payments')"
            name="fade"
          >
            <credit-card-payment-table
              v-if="getPaymentsMeta.item_count > 0"
              ref="table"
              :items="getPaymentsItems"
              :loading="$wait.is('loading infinite payments')"
              :loading-props="{
                loadMore: $t('billing.load_more_payments'),
                loaded: $t('billing.all_cc_payments_loaded')
              }"
              :can-load-more="getPaymentsMeta.item_count && canRetrieveMore && !$wait.is('loading infinite payments')"
              class="tw-flex-1"
              @infinite-scroll="retrieveMore"
            />
            <ctk-empty-state
              v-if="getPaymentsMeta.item_count === 0"
              :title="$t('billing.no_payments')"
            />
          </transition>
        </div>
      </div>
    </div>

    <ctk-loading-layer
      v-if="$wait.is('loading payments')"
      :background="false"
    >
      {{ $t('billing.loading_payments') }}
    </ctk-loading-layer>
  </div>
</template>

<script>
  import { defineComponent } from '@vue/composition-api'
  import { mapGetters, mapActions } from 'vuex'
  import { Payment } from '@/resources'

  import CtkEmptyState from '@/components/CtkEmptyState/index.vue'
  import CtkLoadingLayer from '@/components/CtkLoadingLayer/index.vue'

  import CreditCardPaymentTable from './components/CreditCardPaymentTable/index.vue'
  import InvoicesHeader from '@/views/Common/Billing/_subs/Invoices/_subs/InvoicesHeader/index.vue'
  import useSticky from '@/composables/useSticky'

  import store from '@/store'

  /**
   * @module view - CreditCardPayments
   */
  export default defineComponent({
    name: 'CreditCardPayments',
    components: {
      CtkEmptyState,
      CtkLoadingLayer,
      InvoicesHeader,
      CreditCardPaymentTable
    },
    setup () {
      const { create: updateSticky } = useSticky('.billing-table-header')

      return {
        updateSticky
      }
    },
    // @ts-ignore
    beforeRouteEnter (to, from, next) {
      store.commit('wait/START', 'loading payments')

      if (!store.getters.isUserShipper) {
        next({
          name: 'Offers'
        })
        return
      }

      store.dispatch('billing/retrieveInvoicesMetrics')
      store.dispatch('billing/retrievePaymentsMetrics')
      next()
    },
    computed: {
      ...mapGetters('auth', [
        'getCid'
      ]),
      ...mapGetters('billing', [
        'getPaymentsMeta',
        'getPaymentsItems'
      ]),
      /**
       * @function getCurrentPayment
       */
      getCurrentPayment () {
        return this.$route.params.uuid
      },
      /**
       * Returns true if we can retrive more payments
       * @function canRetrieveMore
       * @returns {boolean} retrieveMore
       */
      canRetrieveMore () {
        const { pagination } = this.getPaymentsMeta
        return pagination.current_page < pagination.page_count
      }
    },
    watch: {
      getCurrentPayment () {
        this.updateInvoices()
      }
    },
    mounted () {
      this.updatePayments()
        .then(() => {
          this.updateInvoices()
        })
    },
    methods: {
      ...mapActions('billing', [
        'retrievePayments',
        'retrieveMorePayments',
        'setCurrentPaymentInvoices'
      ]),
      /**
       * Called whenever the scroll arrives at the end.
       * The purpose of this bifurcation is to handle the mobile fetching
       * separatly since we use the full-page scroll, not on the list.
       * @function retrieveMoreMobile
       */
      retrieveMoreMobile () {
        const width = document.documentElement.clientWidth
        if (width <= 455) {
          this.retrieveMore()
        }
      },
      /**
       * @function retriveMore
       */
      retrieveMore () {
        if (this.canRetrieveMore) {
          this.$wait.start('loading infinite payments')
          this.retrieveMorePayments()
            .finally(() => {
              this.$wait.end('loading infinite payments')
            })
        }
      },
      /**
       * @function updateInvoices
       */
      updateInvoices () {
        if (this.getCurrentPayment) {
          this.$wait.start('fetching payment invoices')
          this.$err.hide('no payment invoices')

          Payment.invoices({
            cid: this.getCid,
            pid: this.getCurrentPayment
          })
            .then((res) => {
              this.setCurrentPaymentInvoices(res.data.items)
            })
            .catch(() => {
              this.$err.show('no payment invoices')
            })
            .finally(() => {
              this.$wait.end('fetching payment invoices')
            })
        }
      },
      /**
       * @function updatePayments
       */
      updatePayments (loader = 'loading payments') {
        this.$wait.start(loader)
        return this.retrievePayments({
          page: 1
        })
          .finally(() => {
            this.updateSticky()
            this.$wait.end(loader)
          })
      }
    }
  })
</script>

<style lang="scss" scoped>
  .credit-card-payments {
    .invoices-header,
    .credit-card-payment-table {
      padding: 0 30px 0 53px;
    }

    .invoices-header::after {
      left: 53px;
      width: calc(100% - 83px);
    }

    .content {
      position: relative;
    }

    @media only screen and (max-width: $breakpoint-mobile-l) {
      .credit-card-payment-table {
        padding: 0 16px;
      }

      &-header {
        padding: 0 16px 0 39px;

        &::after {
          left: 39px;
          width: calc(100% - 55px);
        }
      }
    }
  }
</style>
